import { Observable } from 'rxjs';
import { Component, HostListener, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { BaseInspectionOverview } from 'src/app/DTOs/InspectionMasterData/productDetailModel';
import { tileRangeLabel } from 'src/app/helper/TileRangeLabel';
import { NotificationService } from 'src/app/Shared/prime-ng/notification.service';
import { DataResult, DataStateChangeEvent, GridDataResult, PageChangeEvent, State, pageSkip, pageTake, process } from 'src/app/Shared/prime-ng/grid-data';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';

export interface GridConfig {
  getData: () => Observable<BaseInspectionOverview[]>;
  createItem?: (item: BaseInspectionOverview) => Observable<BaseInspectionOverview>;
  updateItem?: (id: number, item: BaseInspectionOverview) => Observable<BaseInspectionOverview | void>;
  deleteItem?: (id: number) => Observable<BaseInspectionOverview | boolean | void>;
}

@Component({
  selector: 'app-configurable-grid',
  standalone: true,
  imports: [PrimeNgModule, CommonModule,
    FormsModule],
  templateUrl: './configurable-grid.component.html',
  styleUrls: ['./configurable-grid.component.css']
})
export class ConfigurableGridComponent implements OnInit {
  @Input() config!: GridConfig;
  @Input() headerTitle = '';

  // Data
  allData: BaseInspectionOverview[] = [];
  gridView: { data: BaseInspectionOverview[]; total: number } = { data: [], total: 0 };

  // State
  gridState = { skip: 0, take: 12 };
  loading = false;
  searchText = '';
  openActionMenuKey: string | null = null;
  expandedCardKeys = new Set<string>();

  get footerRangeLabel(): string {
    return tileRangeLabel(
      this.gridView.total,
      this.gridState.take,
      this.gridState.skip,
    );
  }

  // Delete Dialog
  deleteDialog = false;
  itemToDelete: BaseInspectionOverview | null = null;

  constructor(private notificationService: NotificationService) { }

  ngOnInit(): void {
    if (!this.config) {
      console.error('Grid config is required!');
      return;
    }
    this.loadData();
  }

  // ==================== Data Loading ====================

  loadData(): void {
    this.loading = true;
    this.config.getData().subscribe({
      next: (res) => {
        this.allData = this.sortItems((res ?? []).filter(item => !item.deleted));
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.notification('Error loading data', 'error');
        this.loading = false;
      }
    });
  }

  // ==================== Search & Filter ====================

  onSearchChange(): void {
    this.gridState.skip = 0;
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchText = '';
    this.gridState.skip = 0;
    this.applyFilters();
  }

  private applyFilters(): void {
    let filtered = this.sortItems(this.allData);

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(item =>
        item.titleDE?.toLowerCase().includes(search) ||
        item.titleEN?.toLowerCase().includes(search)
      );
    }

    filtered = this.sortItems(filtered);

    if (this.gridState.skip >= filtered.length && filtered.length > 0) {
      this.gridState.skip = Math.max(0, Math.floor((filtered.length - 1) / this.gridState.take) * this.gridState.take);
    }

    this.gridView = {
      data: filtered.slice(this.gridState.skip, this.gridState.skip + this.gridState.take),
      total: filtered.length
    };
  }

  // ==================== Pagination ====================

  pageChange(event: PageChangeEvent): void {
    this.gridState.skip = pageSkip(event);
    this.gridState.take = pageTake(event);
    this.applyFilters();
  }

  // ==================== CRUD Operations ====================

  addNew(): void {
    const newItem: BaseInspectionOverview = {
      databaseId: 0,
      titleDE: '',
      titleEN: '',
      naAvailable: false,
      seq: this.getNextSeq(),
      deleted: false
    };
    this.allData = this.sortItems([newItem, ...this.allData]);
    this.gridState.skip = 0;
    this.applyFilters();
  }

  onFieldBlur(dataItem: BaseInspectionOverview): void {
    if (!this.config.createItem || !this.config.updateItem) return;

    const isNew = !dataItem.databaseId || dataItem.databaseId === 0;

    if (isNew && (!dataItem.titleDE?.trim() || !dataItem.titleEN?.trim())) {
      return;
    }

    const save$: Observable<BaseInspectionOverview | void> = isNew
      ? this.config.createItem(dataItem)
      : this.config.updateItem(dataItem.databaseId, dataItem);

    save$.subscribe({
      next: (res) => {
        if (res && typeof res === 'object' && 'databaseId' in res) {
          Object.assign(dataItem, res);
        }
        this.allData = this.sortItems(this.allData);
        this.applyFilters();
        this.notification(isNew ? 'Created successfully' : 'Saved successfully', 'success');
      },
      error: () => this.notification('Error saving', 'error')
    });
  }

  onSwitchChange(dataItem: BaseInspectionOverview, value: boolean): void {
    dataItem.naAvailable = value;
    this.onFieldBlur(dataItem);
  }

  toggleActionMenu(dataItem: BaseInspectionOverview, event: MouseEvent): void {
    event.stopPropagation();
    const key = this.itemKey(dataItem);
    this.openActionMenuKey = this.openActionMenuKey === key ? null : key;
  }

  @HostListener('document:click')
  closeActionMenu(): void {
    this.openActionMenuKey = null;
  }

  isActionMenuOpen(dataItem: BaseInspectionOverview): boolean {
    return this.openActionMenuKey === this.itemKey(dataItem);
  }

  toggleCardExpanded(dataItem: BaseInspectionOverview, event: MouseEvent): void {
    event.stopPropagation();
    const key = this.itemKey(dataItem);

    if (this.expandedCardKeys.has(key)) {
      this.expandedCardKeys.delete(key);
      return;
    }

    this.expandedCardKeys.add(key);
  }

  isCardExpanded(dataItem: BaseInspectionOverview): boolean {
    return this.expandedCardKeys.has(this.itemKey(dataItem));
  }

  openDeleteDialogFromMenu(dataItem: BaseInspectionOverview): void {
    this.openActionMenuKey = null;
    this.openDeleteDialog(dataItem);
  }

  trackByItem(_: number, item: BaseInspectionOverview): number {
    return item.databaseId || item.seq;
  }

  // ==================== Delete ====================

  openDeleteDialog(dataItem: BaseInspectionOverview): void {
    this.itemToDelete = dataItem;
    this.deleteDialog = true;
  }

  toggleDeleteDialog(): void {
    this.deleteDialog = false;
    this.itemToDelete = null;
  }

  confirmDelete(): void {
    if (!this.itemToDelete) {
      this.toggleDeleteDialog();
      return;
    }

    const isNew = !this.itemToDelete.databaseId || this.itemToDelete.databaseId === 0;

    if (isNew) {
      this.allData = this.allData.filter(i => i !== this.itemToDelete);
      this.applyFilters();
      this.toggleDeleteDialog();
      return;
    }

    if (!this.config.deleteItem) {
      this.toggleDeleteDialog();
      return;
    }

    this.config.deleteItem(this.itemToDelete.databaseId).subscribe({
      next: () => {
        this.allData = this.allData.filter(i => i !== this.itemToDelete);
        this.applyFilters();
        this.notification('Deleted successfully', 'success');
        this.toggleDeleteDialog();
      },
      error: () => {
        this.notification('Error deleting', 'error');
        this.toggleDeleteDialog();
      }
    });
  }

  // ==================== Notification ====================

  private notification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: type, icon: true }
    });
  }

  private sortItems(items: BaseInspectionOverview[]): BaseInspectionOverview[] {
    return [...items].sort((a, b) => {
      if (b.seq !== a.seq) {
        return b.seq - a.seq;
      }

      return b.databaseId - a.databaseId;
    });
  }

  private getNextSeq(): number {
    return this.allData.reduce((max, item) => Math.max(max, item.seq ?? 0), 0) + 1;
  }

  private itemKey(item: BaseInspectionOverview): string {
    return `${item.databaseId || 'new'}-${item.seq}`;
  }
}
